import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { subscribeToasts, getToastSnapshot, dismissToast, ToastItem, ToastKind } from "../lib/toast";

// Dark ink toasts as in the design; errors keep a red accent bar so they stand out.
const KIND_CLASSES: Record<ToastKind, { bar: string; dot: string }> = {
  error:   { bar: "bg-status-alert-fg", dot: "bg-waves-koraal" },
  success: { bar: "bg-status-ok-fg",    dot: "bg-[#4ED39A]" },
  info:    { bar: "bg-waves-zee",       dot: "bg-waves-zee" },
};

function ToastRow({ toast }: { toast: ToastItem }) {
  const [paused, setPaused] = useState(false);
  // Total time (ms) this toast has spent paused since toast.createdAt.
  const pausedMsRef = useRef(0);
  // Timestamp when the current pause began, or null if not currently paused.
  const pauseStartRef = useRef<number | null>(null);

  useEffect(() => {
    if (paused) {
      pauseStartRef.current = Date.now();
      return;
    }
    // Resuming: fold the just-finished pause into the accumulated total so it's
    // excluded from the elapsed-active calculation below.
    if (pauseStartRef.current !== null) {
      pausedMsRef.current += Date.now() - pauseStartRef.current;
      pauseStartRef.current = null;
    }
    const elapsedActive = Date.now() - toast.createdAt - pausedMsRef.current;
    const remaining = toast.durationMs - elapsedActive;
    if (remaining <= 0) {
      dismissToast(toast.id);
      return;
    }
    const timer = setTimeout(() => dismissToast(toast.id), remaining);
    return () => clearTimeout(timer);
  }, [toast.id, toast.createdAt, toast.durationMs, paused]);

  const kind = KIND_CLASSES[toast.kind];

  return (
    <div
      role={toast.kind === "error" ? "alert" : "status"}
      data-tour={toast.action ? "toast" : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative flex min-h-12 max-w-md items-center gap-3 overflow-hidden rounded-pill bg-waves-ink py-2 pl-5 pr-2 text-sm font-medium text-white shadow-modal animate-in fade-in-0 slide-in-from-bottom-2"
    >
      <span className={cn("h-2 w-2 shrink-0 rounded-full", kind.dot)} />
      <span className="flex-1">{toast.message}</span>
      {toast.action && (
        <button
          onClick={() => {
            toast.action!.onClick();
            dismissToast(toast.id);
          }}
          className="shrink-0 rounded-pill px-3 py-1.5 text-sm font-bold text-waves-zee transition-colors hover:bg-white/10"
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={() => dismissToast(toast.id)}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill text-white/60 transition-colors hover:bg-white/10 hover:text-white"
        aria-label="Sluiten"
      >
        <X size={14} />
      </button>
      <span
        className={cn("absolute bottom-0 left-0 h-0.5 opacity-70", kind.bar)}
        style={{
          animation: `toast-shrink ${toast.durationMs}ms linear forwards`,
          animationPlayState: paused ? "paused" : "running",
        }}
      />
    </div>
  );
}

export default function Toaster() {
  const toasts = useSyncExternalStore(subscribeToasts, getToastSnapshot);
  if (toasts.length === 0) return null;
  return (
    <div aria-live="polite" className="fixed bottom-6 left-1/2 z-[100] flex -translate-x-1/2 flex-col items-center gap-2 px-4">
      {toasts.map((toast) => <ToastRow key={toast.id} toast={toast} />)}
    </div>
  );
}
